/**
 * DropOptionsSection - Include duplicates, batches, AI Optimization, use only new domains.
 * Batches + AI Optimization are only interactive when enableBatchesAndAi is set (the Draft
 * Creator); elsewhere they stay as disabled "Coming soon" rows.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Zap } from 'lucide-react';

import { OptionRow } from './OptionRow';
import { ShortlinkOptions, type ShortlinkOptionsProps } from './ShortlinkOptions';
import { BatchAndAiOptions } from './batch-ai';

export type DropOptionValue = boolean | 'mixed' | '' | 'unchanged';

export interface DropOptionsSectionProps {
  amount: number | '';
  forceSend: boolean | DropOptionValue;
  setForceSend: (v: boolean | 'unchanged') => void;
  sendInBatches: boolean | DropOptionValue;
  setSendInBatches: (v: boolean | 'unchanged') => void;
  batchAmount: number | undefined;
  setBatchAmount: (v: number | undefined) => void;
  batchDelay: number | undefined;
  setBatchDelay: (v: number | undefined) => void;
  aiOptimizationEnabled: boolean;
  setAiOptimizationEnabled: (v: boolean) => void;
  aiOptimizationType: 'ROI' | 'CTR' | '';
  setAiOptimizationType: (v: 'ROI' | 'CTR') => void;
  aiOptimizationBatchAmount: number | '';
  setAiOptimizationBatchAmount: (v: number | '') => void;
  aiOptimizationMinimalValuePercent: number | '';
  setAiOptimizationMinimalValuePercent: (v: number | '') => void;
  aiOptimizationSendUntil: string;
  setAiOptimizationSendUntil: (v: string) => void;
  useOnlyNewDomains: boolean | DropOptionValue;
  setUseOnlyNewDomains: (v: boolean | 'unchanged') => void;
  isBusy?: boolean;
  compact?: boolean;
  allowUnchanged?: boolean;
  /** Hide the internal "Drop options" heading (when the parent already shows one). */
  hideTitle?: boolean;
  /** Show only Include duplicates (Edit Sent Drop modal). */
  showOnlyForceSend?: boolean;
  /** Turn Send-in-batches + AI Optimization into live controls (Draft Creator only). */
  enableBatchesAndAi?: boolean;
  /** Shortlink click-id structure + domain scheme. Rendered when provided —
   *  also in showOnlyForceSend mode, since SMSWiser lets sent drops change them. */
  shortlink?: Pick<
    ShortlinkOptionsProps,
    'clickIdStructure' | 'setClickIdStructure' | 'domainScheme' | 'setDomainScheme'
  >;
}

const FORCE_SEND_TIP = 'Include recipients who already received an SMS today.';
const USE_ONLY_NEW_TIP = 'Use only domains not used before. Amount must be ≤ 10,000.';

const SHORT_LABELS = {
  forceSend: 'Include duplicates',
  newDomains: 'New domains only',
};

export const DropOptionsSection: FC<DropOptionsSectionProps> = (props) => {
  const {
    amount,
    forceSend,
    setForceSend,
    useOnlyNewDomains,
    setUseOnlyNewDomains,
    isBusy = false,
    compact = false,
    allowUnchanged = false,
    hideTitle = false,
    showOnlyForceSend = false,
    enableBatchesAndAi = false,
    shortlink,
  } = props;

  const amt = typeof amount === 'number' ? amount : 0;
  const disableUseOnlyNew = amt > 10000;
  const batchesOn = props.sendInBatches === true;

  const forceLabel = compact ? SHORT_LABELS.forceSend : 'Include recipients who already received SMS today';
  const newDomainsLabel = compact ? SHORT_LABELS.newDomains : 'Use only new domains';

  return (
    <section className={compact ? 'space-y-1.5' : 'space-y-3'} data-testid="drop-options-section">
      {!hideTitle && (
        <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Zap className="h-3.5 w-3.5" />
          Drop options
        </h4>
      )}
      <div className={compact ? 'grid min-w-0 grid-cols-1 gap-y-2' : 'space-y-2'}>
        <OptionRow
          value={forceSend as DropOptionValue}
          onChange={setForceSend}
          label={forceLabel}
          tooltip={FORCE_SEND_TIP}
          disabled={isBusy}
          allowUnchanged={allowUnchanged}
        />
        {!showOnlyForceSend && (
          <>
            <BatchAndAiOptions
              {...props}
              enableBatchesAndAi={enableBatchesAndAi}
              compact={compact}
              isBusy={isBusy}
              allowUnchanged={allowUnchanged}
            />
            <OptionRow
              value={useOnlyNewDomains as DropOptionValue}
              onChange={setUseOnlyNewDomains}
              label={newDomainsLabel}
              tooltip={USE_ONLY_NEW_TIP}
              disabled={disableUseOnlyNew || isBusy || batchesOn}
              allowUnchanged={allowUnchanged}
            />
          </>
        )}
      </div>
      {shortlink && (
        <ShortlinkOptions
          {...shortlink}
          allowUnchanged={allowUnchanged}
          disabled={isBusy}
          compact={compact}
        />
      )}
    </section>
  );
};

export default DropOptionsSection;
